// Every backend route the SPA addresses, in one place. Builders return the
// path (plus query) only; call sites prefix an origin from lib/tenantOrigin.
// This module must stay dependency-free: it is imported by lib/tracing.ts,
// which Node-based test files pull in transitively, so it can never reach
// environment.ts (which touches document at module load).
// The proxy route families these paths fall under are listed in
// services/shared/apiPathSurfaces.ts.

import type { ExecutionDataKind } from "../../shared/executionDataKinds";

function uiApiPath(path: string): string {
  return `/ui-api${path}`;
}

export function getSessionPath(): string {
  return uiApiPath("/auth/get-session");
}

export function signInSsoPath(): string {
  return uiApiPath("/auth/sign-in/sso");
}

export function signOutPath(): string {
  return uiApiPath("/auth/sign-out");
}

export function nativeCallbackPath(): string {
  return uiApiPath("/auth/native-callback");
}

export function executionContentPath(
  executionId: string,
  field: ExecutionDataKind,
  options: { tail?: boolean; body?: boolean } = {}
): string {
  const params = new URLSearchParams({ field });
  if (options.tail) params.set("tail", "true");
  if (options.body) params.set("body", "1");
  return uiApiPath(
    `/execution-content/${encodeURIComponent(executionId)}?${params}`
  );
}

export function toolContentPath(entryId: string): string {
  return uiApiPath(`/tool-content/${encodeURIComponent(entryId)}`);
}

export function attachmentPath(attachmentId: string): string {
  return uiApiPath(`/attachments/${encodeURIComponent(attachmentId)}`);
}

export function attachmentUploadPath(): string {
  return uiApiPath("/attachments");
}

export function toolCallOutputUploadPath(): string {
  return uiApiPath("/tool-call-outputs");
}

export function chatStreamPath(chatId: string): string {
  return uiApiPath(`/chat-stream/${encodeURIComponent(chatId)}`);
}

export function stepExecutionsPath(): string {
  return uiApiPath("/step-executions");
}

export function runExecutionsPath(): string {
  return uiApiPath("/run-executions");
}

export function monitoringAiUsagePath(): string {
  return uiApiPath("/monitoring/ai-usage");
}

export function monitoringWorkflowsPath(): string {
  return uiApiPath("/monitoring/workflows");
}

export function monitoringWorkflowRoutesPath(): string {
  return uiApiPath("/monitoring/workflow-routes");
}

export function monitoringWorkflowDependenciesPath(): string {
  return uiApiPath("/monitoring/workflow-dependencies");
}

export function monitoringConnectorUsagePath(): string {
  return uiApiPath("/monitoring/connector-usage");
}

export function monitoringWorkflowValuePath(): string {
  return uiApiPath("/monitoring/workflow-value");
}

export function personalVolumePath(volumeName: string): string {
  return uiApiPath(`/personal-volumes/${encodeURIComponent(volumeName)}`);
}

export function spaceVolumePath(
  spaceId: string,
  branchId: string,
  volumeName: string
): string {
  return uiApiPath(
    `/volumes/${encodeURIComponent(spaceId)}/${encodeURIComponent(branchId)}/${encodeURIComponent(volumeName)}`
  );
}

export function volumeFilePath(
  volumeDirPath: string,
  relativePath: string
): string {
  return `${volumeDirPath}/file?path=${encodeURIComponent(relativePath)}`;
}

export function isVolumeFilePath(pathname: string): boolean {
  const volumePath = pathname.replace(/^\/ui-api/, "");
  return (
    (volumePath.startsWith("/volumes/") ||
      volumePath.startsWith("/personal-volumes/")) &&
    volumePath.endsWith("/file")
  );
}

export function userAvatarPath(key: string): string {
  return uiApiPath(`/user-avatars/${encodeURIComponent(key)}`);
}

export function faviconPath(domain: string): string {
  return uiApiPath(`/favicon/${encodeURIComponent(domain)}`);
}

export function telemetryTokenPath(): string {
  return uiApiPath("/telemetry/token");
}

export function telemetryTracesPath(): string {
  return uiApiPath("/telemetry/traces");
}

export function tunnelRepoCredsPath(): string {
  return uiApiPath("/tunnel-repo-creds");
}

export function licensePath(): string {
  return uiApiPath("/license");
}

export function deviceAuthPath(action: "lookup" | "approve" | "deny"): string {
  return `/api/v1/auth/device/${action}`;
}

export const apiDocsPath = "/api/v1/docs";

export function connectorOauthStartPath(identifier: string): string {
  return `/connectors/oauth/${encodeURIComponent(identifier)}/start`;
}

export function spaceGitPath(spaceSlugOrId: string): string {
  return `/spaces/${spaceSlugOrId}.git`;
}

export const scimV2Path = "/scim/v2";
